/**
 * Create / edit project. Supports edit via `?id={id}` query param.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import projectService from '../services/projectService';
import instrumentService from '../services/instrumentService';
import {
  Project,
  PocStatus,
  PipelineStatus,
  InstrumentLibraryItem,
} from '../types/project';

const POC_OPTIONS: Array<{ value: PocStatus; label: string }> = [
  { value: 'concept', label: 'Concept' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'scaling', label: 'Scaling' },
];

const PIPELINE_OPTIONS: Array<{ value: PipelineStatus; label: string }> = [
  { value: 'seeking', label: 'Seeking' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'term_sheet', label: 'Term Sheet' },
  { value: 'closing', label: 'Closing' },
  { value: 'closed', label: 'Closed' },
  { value: 'passed', label: 'Passed' },
];

interface FormState {
  // Identity
  project_name: string;
  description: string;
  startup_id: string;
  is_standalone: boolean;

  // Financial
  funding_needed: string;
  funding_committed: string;

  // Timeline
  current_phase: string;
  start_date: string;
  target_completion: string;

  // Traction
  poc_status: PocStatus | '';
  traction_notes: string;
  revenue_to_date: string;
  loi_count: string;

  // Team
  team_csv: string; // simple "name|role|linkedin" entries, one per line
  advisory_csv: string;

  // IP
  ip_csv: string; // "type|description|status|filing_number"

  // Risk
  risk_factors_csv: string;
  risk_score: string;

  // Revenue Model
  revenue_model: string;
  rev_year1: string;
  rev_year2: string;
  rev_year3: string;
  rev_assumptions: string;

  // Instrument
  selected_instrument: string;
  pipeline_status: PipelineStatus | '';
}

const emptyForm: FormState = {
  project_name: '',
  description: '',
  startup_id: '',
  is_standalone: false,
  funding_needed: '',
  funding_committed: '',
  current_phase: '',
  start_date: '',
  target_completion: '',
  poc_status: '',
  traction_notes: '',
  revenue_to_date: '',
  loi_count: '',
  team_csv: '',
  advisory_csv: '',
  ip_csv: '',
  risk_factors_csv: '',
  risk_score: '',
  revenue_model: '',
  rev_year1: '',
  rev_year2: '',
  rev_year3: '',
  rev_assumptions: '',
  selected_instrument: '',
  pipeline_status: 'seeking',
};

const csvToArray = (value: string): string[] =>
  value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const linesToObjects = (
  raw: string,
  fields: string[]
): Array<Record<string, string>> => {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split('|').map((p) => p.trim());
      const obj: Record<string, string> = {};
      fields.forEach((f, idx) => {
        if (parts[idx]) obj[f] = parts[idx];
      });
      return obj;
    });
};

const objectsToLines = (
  arr: Array<Record<string, any>> | undefined,
  fields: string[]
): string => {
  if (!arr || arr.length === 0) return '';
  return arr
    .map((obj) => fields.map((f) => (obj[f] !== undefined ? String(obj[f]) : '')).join(' | '))
    .join('\n');
};

const toNumber = (value: string): number | undefined => {
  if (value === '' || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const projectToForm = (p: Project): FormState => ({
  project_name: p.project_name || '',
  description: p.description || '',
  startup_id: p.startup_id !== undefined ? String(p.startup_id) : '',
  is_standalone: !!p.is_standalone,
  funding_needed: p.funding_needed !== undefined ? String(p.funding_needed) : '',
  funding_committed:
    p.funding_committed !== undefined ? String(p.funding_committed) : '',
  current_phase: p.current_phase || '',
  start_date: p.start_date || '',
  target_completion: p.target_completion || '',
  poc_status: p.poc_status || '',
  traction_notes: p.traction_notes || '',
  revenue_to_date:
    p.revenue_to_date !== undefined ? String(p.revenue_to_date) : '',
  loi_count: p.loi_count !== undefined ? String(p.loi_count) : '',
  team_csv: objectsToLines(p.team_members, ['name', 'role', 'linkedin_url']),
  advisory_csv: objectsToLines(p.advisory_board, ['name', 'role', 'linkedin_url']),
  ip_csv: objectsToLines(p.ip_assets, ['type', 'description', 'status', 'filing_number']),
  risk_factors_csv: (p.risk_factors || []).join(', '),
  risk_score: p.risk_score !== undefined ? String(p.risk_score) : '',
  revenue_model: p.revenue_model || '',
  rev_year1:
    p.revenue_projections?.year1 !== undefined
      ? String(p.revenue_projections.year1)
      : '',
  rev_year2:
    p.revenue_projections?.year2 !== undefined
      ? String(p.revenue_projections.year2)
      : '',
  rev_year3:
    p.revenue_projections?.year3 !== undefined
      ? String(p.revenue_projections.year3)
      : '',
  rev_assumptions: p.revenue_projections?.assumptions || '',
  selected_instrument: p.selected_instrument || '',
  pipeline_status: p.pipeline_status || 'seeking',
});

const formToPayload = (form: FormState): Partial<Project> => {
  const startupId = toNumber(form.startup_id);
  const team_members = linesToObjects(form.team_csv, ['name', 'role', 'linkedin_url']);
  const advisory_board = linesToObjects(form.advisory_csv, [
    'name',
    'role',
    'linkedin_url',
  ]);
  const ip_assets = linesToObjects(form.ip_csv, [
    'type',
    'description',
    'status',
    'filing_number',
  ]);
  const risk_factors = csvToArray(form.risk_factors_csv);

  const rev1 = toNumber(form.rev_year1);
  const rev2 = toNumber(form.rev_year2);
  const rev3 = toNumber(form.rev_year3);
  const projections =
    rev1 !== undefined ||
    rev2 !== undefined ||
    rev3 !== undefined ||
    form.rev_assumptions.trim()
      ? {
          year1: rev1,
          year2: rev2,
          year3: rev3,
          assumptions: form.rev_assumptions.trim() || undefined,
        }
      : undefined;

  return {
    project_name: form.project_name.trim(),
    description: form.description.trim() || undefined,
    startup_id: startupId,
    is_standalone: form.is_standalone,
    funding_needed: toNumber(form.funding_needed),
    funding_committed: toNumber(form.funding_committed),
    current_phase: form.current_phase.trim() || undefined,
    start_date: form.start_date || undefined,
    target_completion: form.target_completion || undefined,
    poc_status: form.poc_status || undefined,
    traction_notes: form.traction_notes.trim() || undefined,
    revenue_to_date: toNumber(form.revenue_to_date),
    loi_count: toNumber(form.loi_count),
    team_members: team_members.length ? team_members : undefined,
    advisory_board: advisory_board.length ? advisory_board : undefined,
    ip_assets: ip_assets.length ? ip_assets : undefined,
    risk_factors: risk_factors.length ? risk_factors : undefined,
    risk_score: toNumber(form.risk_score),
    revenue_model: form.revenue_model.trim() || undefined,
    revenue_projections: projections,
    selected_instrument: form.selected_instrument || undefined,
    pipeline_status: form.pipeline_status || undefined,
  };
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
      {title}
    </h2>
    {children}
  </div>
);

const NewProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEdit = !!editId;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [isLoading, setIsLoading] = useState<boolean>(isEdit);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instruments, setInstruments] = useState<InstrumentLibraryItem[]>([]);

  useEffect(() => {
    const loadInstruments = async () => {
      const result = await instrumentService.fetchInstruments();
      if (!result.error && result.data) {
        setInstruments(result.data.instruments);
      }
    };
    loadInstruments();
  }, []);

  useEffect(() => {
    if (!isEdit || !editId) return;
    const load = async () => {
      setIsLoading(true);
      const result = await projectService.fetchProject(parseInt(editId, 10));
      if (result.error) {
        toast.error(result.error);
      } else if (result.data) {
        setForm(projectToForm(result.data));
      }
      setIsLoading(false);
    };
    load();
  }, [isEdit, editId]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.project_name.trim()) {
      toast.error('Project name is required.');
      return;
    }
    setIsSaving(true);
    const payload = formToPayload(form);
    const result =
      isEdit && editId
        ? await projectService.updateProject(parseInt(editId, 10), payload)
        : await projectService.createProject(payload);
    setIsSaving(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success(isEdit ? 'Project updated' : 'Project created');
    if (result.data) {
      navigate(`/projects/${result.data.id}`);
    } else {
      navigate('/projects');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading project...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit} className="space-y-5 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link
            to="/projects"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to projects
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEdit ? 'Save changes' : 'Create project'}
          </button>
        </div>

        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit project' : 'New project'}
        </h1>

        <Section title="Identity">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Project name *</label>
              <input
                type="text"
                value={form.project_name}
                onChange={(e) => update('project_name', e.target.value)}
                required
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Startup ID (optional)</label>
              <input
                type="number"
                min="1"
                value={form.startup_id}
                onChange={(e) => update('startup_id', e.target.value)}
                disabled={form.is_standalone}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="is_standalone"
                type="checkbox"
                checked={form.is_standalone}
                onChange={(e) => {
                  update('is_standalone', e.target.checked);
                  if (e.target.checked) update('startup_id', '');
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="is_standalone" className="text-sm text-gray-700">
                Standalone project (no parent startup)
              </label>
            </div>
          </div>
        </Section>

        <Section title="Financial">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Funding needed (USD)</label>
              <input
                type="number"
                min="0"
                value={form.funding_needed}
                onChange={(e) => update('funding_needed', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Funding committed (USD)</label>
              <input
                type="number"
                min="0"
                value={form.funding_committed}
                onChange={(e) => update('funding_committed', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </Section>

        <Section title="Timeline">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Current phase</label>
              <input
                type="text"
                value={form.current_phase}
                onChange={(e) => update('current_phase', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Start date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => update('start_date', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Target completion</label>
              <input
                type="date"
                value={form.target_completion}
                onChange={(e) => update('target_completion', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </Section>

        <Section title="Traction">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">POC status</label>
              <select
                value={form.poc_status}
                onChange={(e) => update('poc_status', e.target.value as PocStatus | '')}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">—</option>
                {POC_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Revenue to date (USD)</label>
              <input
                type="number"
                min="0"
                value={form.revenue_to_date}
                onChange={(e) => update('revenue_to_date', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">LOI count</label>
              <input
                type="number"
                min="0"
                value={form.loi_count}
                onChange={(e) => update('loi_count', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-600 mb-1">Traction notes</label>
              <textarea
                value={form.traction_notes}
                onChange={(e) => update('traction_notes', e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </Section>

        <Section title="Team">
          <p className="text-xs text-gray-500">
            One per line, format: <code>name | role | linkedin_url</code>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Team members</label>
              <textarea
                value={form.team_csv}
                onChange={(e) => update('team_csv', e.target.value)}
                rows={4}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Advisory board</label>
              <textarea
                value={form.advisory_csv}
                onChange={(e) => update('advisory_csv', e.target.value)}
                rows={4}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </Section>

        <Section title="IP">
          <p className="text-xs text-gray-500">
            One per line, format: <code>type | description | status | filing_number</code>
          </p>
          <textarea
            value={form.ip_csv}
            onChange={(e) => update('ip_csv', e.target.value)}
            rows={4}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </Section>

        <Section title="Risk">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">
                Risk factors (comma-separated)
              </label>
              <input
                type="text"
                value={form.risk_factors_csv}
                onChange={(e) => update('risk_factors_csv', e.target.value)}
                placeholder="e.g. regulatory delay, key-person risk, dilution-sensitive"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Risk score (0-100)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.risk_score}
                onChange={(e) => update('risk_score', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </Section>

        <Section title="Revenue Model">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Revenue model</label>
              <textarea
                value={form.revenue_model}
                onChange={(e) => update('revenue_model', e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Year 1 revenue</label>
              <input
                type="number"
                min="0"
                value={form.rev_year1}
                onChange={(e) => update('rev_year1', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Year 2 revenue</label>
              <input
                type="number"
                min="0"
                value={form.rev_year2}
                onChange={(e) => update('rev_year2', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Year 3 revenue</label>
              <input
                type="number"
                min="0"
                value={form.rev_year3}
                onChange={(e) => update('rev_year3', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Assumptions</label>
              <textarea
                value={form.rev_assumptions}
                onChange={(e) => update('rev_assumptions', e.target.value)}
                rows={2}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </Section>

        <Section title="Instrument & Pipeline">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Selected instrument</label>
              <select
                value={form.selected_instrument}
                onChange={(e) => update('selected_instrument', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">— None selected —</option>
                {instruments.map((inst) => (
                  <option key={inst.instrument_type} value={inst.instrument_type}>
                    {inst.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pipeline status</label>
              <select
                value={form.pipeline_status}
                onChange={(e) =>
                  update('pipeline_status', e.target.value as PipelineStatus | '')
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PIPELINE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Section>

        <div className="flex items-center justify-end gap-2">
          <Link
            to="/projects"
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEdit ? 'Save changes' : 'Create project'}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
};

export default NewProjectPage;
